import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { CuerpoNavbar, CuerpoAccent } from '@/components/oposiciones/CuerpoNavbar';
import { PlanRedirect } from '@/components/routing/PlanRedirect';
import { Sidebar } from '@/components/sidebar/Sidebar';
import { MobileSidebarDrawer } from '@/components/sidebar/MobileSidebarDrawer';
import { UserMenuButton } from '@/components/chat/UserMenuButton';
import { GlobalGenerationManager } from '@/components/oposiciones/GlobalGenerationManager';
import { GenerationReadyNotifier } from '@/components/notifications/GenerationReadyNotifier';
import { prefetchArea } from '@/lib/routePrefetch';

const AREA_PREFETCH_KEY: Record<string, string> = {
  iipp: 'iipp',
  c2: 'c2',
  guardia_civil: 'gc',
  generico: 'generico',
};

interface CuerpoLayoutProps {
  basePath: string;
  shortLabel: string;
  badgeImage?: string;
  accent: CuerpoAccent;
  areaId: string;
  downloadsMode: any;
  caseLabel?: string;
  hideCase?: boolean;
  extraNavItems?: { id: string; label: string; icon: LucideIcon; path: string }[];
  hideCaso?: boolean;
}

export function CuerpoLayout(props: CuerpoLayoutProps) {
  const location = useLocation();
  const isChatRoute = location.pathname.startsWith(`${props.basePath}/chat`);

  useEffect(() => {
    const key = AREA_PREFETCH_KEY[props.areaId];
    if (key) prefetchArea(key);
  }, [props.areaId]);

  return (
    <div className="flex flex-col h-screen bg-background">
      <PlanRedirect />
      <GenerationReadyNotifier />
      <GlobalGenerationManager />
      <CuerpoNavbar {...props} />
      <div className="flex-1 flex overflow-hidden min-w-0">
        {/* Desktop sidebar in chat routes */}
        {isChatRoute && (
          <div className="hidden md:block oposiciones-sidebar">
            <Sidebar variant="oposiciones" />
          </div>
        )}
        <main className="flex-1 flex flex-col overflow-hidden min-w-0">
          {/* Mobile drawer trigger only on chat routes */}
          {isChatRoute && (
            <div className="md:hidden flex items-center justify-between gap-3 border-b border-border bg-card px-2 py-2 shrink-0 oposiciones-sidebar">
              <div className="flex items-center gap-2 min-w-0">
                <MobileSidebarDrawer variant="oposiciones" triggerClassName="" />
                <span className="text-sm font-semibold truncate">Conversaciones</span>
              </div>
              <UserMenuButton variant="oposiciones" />
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
